import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import apiService from "../services/api";
import Carousel from "react-bootstrap/Carousel";
import "bootstrap/dist/css/bootstrap.min.css";
import SEO from "../components/SEO";
import {
  generateOrganizationSchema,
  generateSiteNavigationSchema,
} from "../utils/seoHelpers";
import { AiOutlineArrowRight, AiOutlineArrowLeft } from "react-icons/ai";

const HomePageWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Carousel Styles
const CarouselSection = styled.section`
  background: white;
  width: 100%;

  @media (max-width: 768px) {
    margin-top: -30px;
  }
`;

const StyledCarousel = styled(Carousel)`
  width: 100%;
  overflow: hidden;
  background: white;

  .carousel-item {
    height: auto;
    position: relative;
  }

  .carousel-indicators {
    bottom: 20px;
    z-index: 3;

    button {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid white;
      background-color: rgba(255, 255, 255, 0.5);

      &.active {
        background-color: #ff6720;
        border-color: #ff6720;
      }
    }
  }

  /* Custom navigation arrows styling */
  .carousel-control-prev,
  .carousel-control-next {
    width: 50px;
    height: 50px;
    background-color: rgba(255, 103, 32, 0.8);
    border-radius: 50%;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.8;
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(255, 103, 32, 1);
      opacity: 1;
    }

    .carousel-control-prev-icon,
    .carousel-control-next-icon {
      width: 20px;
      height: 20px;
    }
  }

  .carousel-control-prev {
    left: 20px;
  }

  .carousel-control-next {
    right: 20px;
  }

  @media (max-width: 768px) {
    .carousel-control-prev,
    .carousel-control-next {
      width: 30px;
      height: 30px;
      top: auto;
      bottom: 15px;
      transform: none;
      left: auto;

      .carousel-control-prev-icon,
      .carousel-control-next-icon {
        width: 16px;
        height: 16px;
      }
    }

    .carousel-control-prev {
      right: 70px;
    }

    .carousel-control-next {
      right: 20px;
    }

    .carousel-indicators {
      button {
        width: 8px; /* Reduced size for responsive */
        height: 8px; /* Reduced size for responsive */
        margin-bottom: -50px; /* Move indicators below controls */
      }
    }
  }
`;

const CarouselSlide = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
`;

// Text section at the top
const SlideTextSection = styled.div`
  background: #ffffffff;
  text-align: left;
  padding-bottom: 20px;

  @media (max-width: 768px) {
    padding: 20px 0;
    text-align: center;
  }
`;

const SlideTextContent = styled.div`
  max-width: 1300px;
  display: flex;
  margin: 0 auto;
  padding: 0 20px;
  height: fit-content !important;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 30px;
    align-items: start;
    text-align: start;
    margin-bottom: 0px;
  }

  @media (max-width: 480px) {
    padding: 0 20px;
  }
`;

const SlideTagline = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 15px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 0px;
  }
`;

const SlideTitle = styled.h2`
  font-size: 48px;
  font-weight: 900;
  margin-bottom: 25px;
  line-height: 1.2;
  color: #333;
  max-width: 800px;

  @media (max-width: 1024px) {
    font-size: 40px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
    max-width: none;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

// Buttons for the text section (top)
const SlideTextButtons = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: -15px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const SlideTextPrimaryButton = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  width: fit-content;
  height: fit-content;
  padding: 12px 28px;
  border-radius: 50px;
  font-weight: bold;
  fontfamily: roboto, sans-serif;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 103, 32, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: #e55a1c;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 103, 32, 0.4);
    color: white;
  }

  @media (max-width: 480px) {
    padding: 10px 30px;
    font-size: 15px;
    width: auto;
    text-align: center;
  }
`;

// Image section below with buttons positioned at left bottom
const SlideImageSection = styled.div`
  height: 100vh;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.mediaType === "image" &&
    props.bgImage &&
    `
    background-image: url(${props.bgImage});
  `}

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.bgGradient || "rgba(0, 0, 0, 0)"};
    z-index: 1;
  }

  /* Video styles */
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
  }

  @media (max-width: 768px) {
    height: 400px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

// Icon Row Section below carousel
const IconRowSection = styled.section`
  background: #f2f2f2;
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  gap: 60px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 40px;
    padding: 30px 20px;
  }

  @media (max-width: 480px) {
    gap: 30px;
    flex-direction: column;
    padding: 25px 20px;
  }
`;

const IconItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #333;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const IconLabel = styled.span`
  font-weight: 600;
  color: #333;
  font-size: 16px;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ShopByCategorySection = styled.section`
  background: #ffffff;
  padding: 30px 0;
  overflow-x: hidden; /* Prevent horizontal overflow */

  @media (max-width: 768px) {
    padding: 50px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const CategoryContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  overflow: hidden; /* Add this to contain any overflow */
`;

const CategoryTitle1 = styled.h2`
  font-size: 48px;
  font-weight: bold;
  color: #333;
  margin-top: 20px;
  margin-bottom: 10px;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;

const CarouselWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden; /* Add this to prevent overflow */
  width: 100%; /* Ensure it doesn't exceed parent width */
`;

const CategoriesScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: auto;
  gap: 20px;
  padding: 20px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Prevent container from extending beyond parent */
  max-width: 100%;
  width: 100%;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const CategoryCardBox = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: transparent;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #5c5c5c83;
  width: 250px;
  border-radius: 10px;
  justify-content: space-between;
  height: 230px;
  padding-bottom: 60px;
  overflow: hidden;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    background: #ffffff;
    color: inherit;
    border-color: transparent;
  }

  @media (max-width: 768px) {
    min-width: 180px;
    padding: 25px 15px;
  }

  @media (max-width: 480px) {
    max-width: 20px !important;
    height: 150px;
    border: 1px solid #5c5c5c83;
  }
`;

const CategoryIcon = styled.div`
  width: 100%;
  height: 100%;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 60px;
    margin-bottom: 15px;
  }
`;

const CategoryName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.4;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #333;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6720;
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: #333;
      transform: translateY(-50%) scale(1);
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const LeftArrow = styled(ArrowButton)`
  left: 15px;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    left: 10px;
  }
`;

const RightArrow = styled(ArrowButton)`
  right: 15px;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    right: 10px;
  }
`;

const ProgressBarContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 30px;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    margin-top: 0px;
    padding: 0px;
  }
`;

const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 2px;
  background-color: #e0e0e0;
  border-radius: 2px;
`;

const ProgressFill = styled.div`
  position: absolute;
  height: 100%;
  width: 30px;
  background-color: #333;
  border-radius: 2px;
  transition: left 0.3s ease;
  left: ${(props) => `calc((100% - 30px) * ${props.progress / 100})`};
`;

const ArrowButtonMobile = styled.button`
  width: 36px;
  height: 36px;
  background: transparent;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 18px;
  font-weight: 300;

  &:hover:not(:disabled) {
    color: #333;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ImageInSection = styled.section`
  width: 100%;
  height: 800px; /* FIXED HEIGHT - No viewport units */
  position: relative;
  overflow: hidden;

  .container {
    height: 100%;
    position: relative;
    max-width: none;
    width: 100%;
    padding: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: bottom;
    }
  }

  /* Keep same height across ALL breakpoints */
  @media (max-width: 1024px) {
    height: 600px; /* Same height */
  }

  @media (max-width: 768px) {
    height: 600px; /* Same height */
  }

  @media (max-width: 480px) {
    height: 400px; /* Only reduce for very small screens */
  }
`;

const ProductHotspot = styled.div`
  position: absolute;
  width: ${(props) => (props.isMobile ? "16px" : "20px")};
  height: ${(props) => (props.isMobile ? "16px" : "20px")};
  background: rgba(255, 255, 255, 0.9);
  border: 3px solid #ff6720;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1000;
  animation: pulse 2s infinite;
  will-change: left, top;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${(props) => (props.isMobile ? "6px" : "8px")};
    height: ${(props) => (props.isMobile ? "6px" : "8px")};
    background: #ff6720;
    border-radius: 50%;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 103, 32, 0.7);
    }
    70% {
      box-shadow: 0 0 0 ${(props) => (props.isMobile ? "8px" : "10px")}
        rgba(255, 103, 32, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 103, 32, 0);
    }
  }
`;

const ProductPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  min-width: 250px;
  margin-bottom: 10px;
  z-index: 10;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-top-color: white;
  }
`;

const PopupImage = styled.img`
  width: 90px !important;
  height: 90px !important;
  object-fit: contain;
  border-radius: 8px;
  margin-right: 15px;
`;

const PopupContent = styled.div`
  display: flex;
  align-items: center;
`;

const PopupDetails = styled.div`
  flex: 1;
`;

const PopupTitle = styled.h4`
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 4px 0;
  color: #333;
`;

const PopupPrice = styled.p`
  font-size: 16px;
  font-weight: bold;
  color: #ff6720;
  margin: 0 0 8px 0;
`;

const PopupButton = styled(Link)`
  background: #ff6720;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 12px;
  font-weight: bold;
  display: inline-block;

  &:hover {
    background: #e55a1c;
    color: white;
  }
`;

const CategoryGrid = styled.section`
  padding: 80px 0;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 50px 0;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 36px;
  font-weight: bold;
  color: #333;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled(Link)`
  display: block;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${(props) => props.bgColor || "#f5f5f5"};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    right: 0;
    width: 50px;
    height: 50px;
    background: ${(props) => props.accentColor || "#ff6720"};
    border-radius: 50px 0 0 0;
  }
`;

const CategoryContent = styled.div`
  padding: 25px;
`;

const CategoryTitle = styled.h3`
  font-size: 22px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
`;

const CategoryDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 15px;
`;

const CategoryLink = styled.span`
  color: #ff6720;
  font-weight: bold;
  font-size: 14px;
  text-decoration: underline;
`;

const FeaturedSection = styled.section`
  background: white;
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 50px 0;
  }
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FeaturedCard = styled.div`
  display: flex;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FeaturedImage = styled.div`
  width: 160px;
  height: 120px;
  background: #f0f0f0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 200px;
  }
`;

const FeaturedContent = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FeaturedTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
`;

const FeaturedDescription = styled.p`
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 10px;
`;

const FeaturedLink = styled(Link)`
  color: #ff6720;
  font-size: 13px;
  text-decoration: underline;

  &:hover {
    color: #e55a1c;
  }
`;

// Carousel data
const carouselData = [
  {
    id: 1,
    tagline: "Professional Tools Made Easy",
    title: "Your personal workshop companion",
    description:
      "Discover premium collection of professional-grade tools designed for demanding projects and everyday tasks.",
    primaryButtonText: "Shop Professional Tools",
    primaryButtonLink: "/collections/professional",
    secondaryButtonText: "How To Use",
    secondaryButtonLink: "/about",
    mediaType: "video", // First slide is VIDEO
    videoUrl:
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/STIHLMS363CHAINSAW.mp4", // Your video file path
    posterImage:
      "https://stihlusa-images.imgix.net/Product/933670/0ff79a0bd42b9b568bcf7f116c788.png", // Fallback image
  },
  {
    id: 2,
    tagline: "Outdoor Solutions Made Simple",
    title: "Transform your outdoor space",
    description:
      "Complete range of garden tools and equipment to maintain and beautify your outdoor environment with ease.",
    primaryButtonText: "Explore Garden Tools",
    primaryButtonLink: "/collections/garden",
    secondaryButtonText: "Learn More",
    secondaryButtonLink: "/collections/garden",
    mediaType: "image", // Second slide is PHOTO
    bgImage:
      "https://www.stihl.in/content/dam/stihl/media/pim/13734.jpg.transform/StihlImageGallery-mq4/img.jpg",
  },
  {
    id: 3,
    tagline: "Limited Time Offers",
    title: "Incredible deals on quality tools",
    description:
      "Save big on selected premium tools and equipment. Professional quality at unbeatable prices for a limited time.",
    primaryButtonText: "View All Deals",
    primaryButtonLink: "/collections/deals",
    secondaryButtonText: "Compare Products",
    secondaryButtonLink: "/compare",
    mediaType: "image", // Third slide is PHOTO
    bgImage:
      "https://www.stihl.in/content/dam/stihl/media/pim/9917.jpg.transform/StihlImageGallery-mq4/img.jpg",
  },
];

const shopCategoryData = [
  {
    id: "chainsaws",
    name: "Chainsaws",
    image:
      "https://cdnassets.stihlusa.com/1736882560-ms162.jpg?fit=crop&fm=webp&h=366&w=482",
    link: "/collections/chainsaws",
  },
  {
    id: "blowers",
    name: "Blowers",
    image:
      "https://cdnassets.stihlusa.com/1754679812-bga57.jpg?fit=crop&fm=webp&h=366&w=482",
    link: "/collections/blowers",
  },
  {
    id: "hand-tools",
    name: "Hand Tools",
    image:
      "https://images.unsplash.com/photo-1516849677043-ef67c9557e16?w=80&h=80&fit=crop",
    link: "/collections/hand-tools",
  },
  {
    id: "outdoor-equipment",
    name: "Outdoor Equipment",
    image:
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=80&h=80&fit=crop",
    link: "/collections/outdoor-equipment",
  },
  {
    id: "gardening",
    name: "Gardening",
    image:
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=80&h=80&fit=crop",
    link: "/collections/gardening",
  },
  {
    id: "handheld-vacuums",
    name: "Handheld Vacuums",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=80&h=80&fit=crop",
    link: "/collections/handheld-vacuums",
  },
  {
    id: "cordless-stick-vacuums",
    name: "Cordless Stick Vacuums",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=80&h=80&fit=crop",
    link: "/collections/cordless-stick-vacuums",
  },
  {
    id: "replacement-filters",
    name: "Replacement Filters",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=80&h=80&fit=crop",
    link: "/collections/replacement-filters",
  },
  {
    id: "storage",
    name: "Storage",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=80&h=80&fit=crop",
    link: "/collections/storage",
  },
];

const responsiveImageHotspots = [
  {
    id: "chainsaw",
    desktop: { x: "55%", y: "85%" }, // Adjusted for desktop image layout
    mobile: { x: "50%", y: "80%" }, // Adjusted for mobile image layout
    product: {
      name: "MS 271 Chainsaw",
      price: "$449.99",
      image:
        "https://stihlusa-images.imgix.net/Product/933670/0ff79a0bd42b9b568bcf7f116c788.png",
      link: "/products/ms-271-chainsaw",
    },
  },
  {
    id: "blower",
    desktop: { x: "10%", y: "80%" },
    mobile: { x: "15%", y: "75%" },
    product: {
      name: "BR 600 Blower",
      price: "$429.99",
      image: "https://cdnassets.stihlusa.com/1754679812-bga57.jpg",
      link: "/products/br-600-blower",
    },
  },
  {
    id: "trimmer",
    desktop: { x: "40%", y: "70%" },
    mobile: { x: "35%", y: "65%" },
    product: {
      name: "FS 131 Trimmer",
      price: "$319.99",
      image:
        "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=80&h=80&fit=crop",
      link: "/products/fs-131-trimmer",
    },
  },
  {
    id: "mower",
    desktop: { x: "85%", y: "80%" },
    mobile: { x: "80%", y: "75%" },
    product: {
      name: "RMA 510 Mower",
      price: "$649.99",
      image:
        "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=80&h=80&fit=crop",
      link: "/products/rma-510-mower",
    },
  },
];

const categoryData = [
  {
    id: "chainsaws",
    title: "Chainsaws",
    description:
      "Professional petrol, electric, and battery chainsaws for every cutting task and application.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/chainsaws",
    bgColor: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
    accentColor: "#991b1b",
  },
  {
    id: "brushcutters",
    title: "Brushcutters",
    description:
      "Heavy-duty brushcutters and trimmers for maintaining large areas and tough vegetation.",
    image:
      "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop",
    link: "/collections/brushcutters",
    bgColor: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
    accentColor: "#16a34a",
  },
  {
    id: "blowers",
    title: "Leaf Blowers",
    description:
      "Powerful petrol, electric, and battery blowers for efficient debris clearing.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/blowers",
    bgColor: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    accentColor: "#0e7490",
  },
  {
    id: "pressure-washers",
    title: "Pressure Washers",
    description:
      "High-pressure cleaning equipment for professional and domestic cleaning applications.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/pressure-washers",
    bgColor: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    accentColor: "#1d4ed8",
  },
  {
    id: "lawn-mowers",
    title: "Lawn Mowers",
    description:
      "Self-propelled, push, and ride-on mowers in petrol, electric, and battery options.",
    image:
      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
    link: "/collections/lawn-mowers",
    bgColor: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    accentColor: "#047857",
  },
];

// const featuredProducts = [
//   {
//     title: 'Professional Petrol Chainsaw',
//     description: 'Heavy-duty performance for demanding cutting tasks',
//     link: '/products/petrol-chainsaw-pro',
//     linkText: 'Learn More',
//     image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=160&h=120&fit=crop'
//   },
//   {
//     title: 'Battery Brushcutter Range',
//     description: 'Cordless convenience for easy vegetation management',
//     link: '/collections/battery-brushcutters',
//     linkText: 'Explore Range',
//     image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=160&h=120&fit=crop'
//   },
//   {
//     title: '3000 PSI Pressure Washer',
//     description: 'Professional cleaning power for tough jobs',
//     link: '/products/pressure-washer-3000psi',
//     linkText: 'Discover Now',
//     image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=160&h=120&fit=crop'
//   }
// ];

const HomePage = () => {
  const [featuredProductsData, setFeaturedProductsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isResizing, setIsResizing] = useState(false);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(
    window.innerWidth <= 480
      ? "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/4mobilescaled.png"
      : "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/16ratioscaled.png",
  );
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [hotspotPositions, setHotspotPositions] = useState([]);
  const imageRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [productsResponse, categoriesResponse] = await Promise.all([
          apiService.getFeaturedProducts(3),
          apiService.getCategories(),
        ]);
        if (productsResponse.success) {
          setFeaturedProductsData(productsResponse.data || []);
        }
        if (categoriesResponse.success) {
          setCategoriesData(categoriesResponse.data || []);
        }
      } catch (err) {
        console.error("Failed to load homepage data:", err);
        setError(err.message);
        setCategoriesData(categoryData);
        setFeaturedProductsData([
          {
            id: "fallback-1",
            name: "Professional Power Tools",
            short_description: "High-quality tools for every project",
            images: [
              {
                image_url:
                  "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=160&h=120&fit=crop",
                alt_text: "Power Tools",
              },
            ],
            price: 199.99,
            slug: "power-tools",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    // Preload images
    const mobileImage = new Image();
    mobileImage.src =
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png";
    const desktopImage = new Image();
    desktopImage.src =
      "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png";
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);

      // Calculate scroll progress percentage
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setScrollProgress(progress);
    }
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -220, behavior: "auto" });
      setTimeout(handleScroll, 300);
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 220, behavior: "auto" });
      setTimeout(handleScroll, 300);
    }
  };

  //new one
  const calculateHotspotPositions = () => {
    const img = imageRef.current;
    if (!img || !imageLoaded) return;

    const cw = img.clientWidth;
    const ch = img.clientHeight;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;

    if (!nw || !nh) return;

    const s = Math.max(cw / nw, ch / nh); // Scale factor for cover
    const dw = nw * s; // Displayed width
    const dh = nh * s; // Displayed height

    const leftOffset = (cw - dw) / 2; // Horizontal center offset
    const topOffset = ch - dh; // Vertical bottom offset

    const positions = responsiveImageHotspots.map((hotspot) => {
      const pos = isMobile ? hotspot.mobile : hotspot.desktop;
      const imageX = (parseFloat(pos.x) / 100) * nw;
      const imageY = (parseFloat(pos.y) / 100) * nh;

      const dx = imageX * s + leftOffset;
      const dy = imageY * s + topOffset;

      const cx = (dx / cw) * 100;
      const cy = (dy / ch) * 100;

      // Clamp to 0-100% to prevent off-screen positions
      return {
        x: `${Math.min(Math.max(cx, 0), 100)}%`,
        y: `${Math.min(Math.max(cy, 0), 100)}%`,
      };
    });

    setHotspotPositions(positions);
  };

  useEffect(() => {
    calculateHotspotPositions();
    window.addEventListener("resize", calculateHotspotPositions);
    return () =>
      window.removeEventListener("resize", calculateHotspotPositions);
  }, [imageLoaded, isMobile, windowWidth]);

  useEffect(() => {
    const checkInitialScroll = () => {
      if (scrollRef.current) {
        handleScroll();
      }
    };
    setTimeout(checkInitialScroll, 100);
  }, []);

  useEffect(() => {
    let resizeTimeout;
    const checkScreenSize = () => {
      setIsResizing(true);
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const width = window.innerWidth;
        setWindowWidth(width);
        setIsMobile(width <= 480);
        setImageSrc(
          width <= 480
            ? "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png"
            : "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/image2.png",
        );
        setIsResizing(false);
      }, 500);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => {
      window.removeEventListener("resize", checkScreenSize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  if (loading) {
    return (
      <HomePageWrapper>
        <div style={{ padding: "100px 20px", textAlign: "center" }}>
          <h2>Loading...</h2>
          <p>Fetching the latest products and categories...</p>
        </div>
      </HomePageWrapper>
    );
  }

  return (
    <HomePageWrapper>
      <SEO
        title="Home | XPLOD-GROUPS - Professional Power Tools & Garden Equipment Kerala"
        description="Shop professional power tools, chainsaws, blowers, lawn mowers, and garden equipment in Kerala. Authorized STIHL dealer with expert service and support."
        keywords="power tools Kerala, STIHL Kerala, chainsaws, blowers, garden tools, professional tools, lawn mowers, Pathanamthitta, authorized dealer"
        canonical="/"
        structuredData={[
          generateOrganizationSchema(),
          generateSiteNavigationSchema(),
        ]}
      />
      {/* Carousel Section */}
      <CarouselSection>
        <div
          className="container"
          style={{ maxWidth: "none", width: "100%", padding: "0" }}
        >
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "0 20px",
              marginBottom: "40px",
            }}
          >
            {/* <SectionTitle>Featured Collections</SectionTitle> */}
          </div>
          <StyledCarousel
            interval={5000}
            controls={true}
            indicators
            pause="hover"
            wrap={true}
          >
            {carouselData.map((slide) => (
              <Carousel.Item key={slide.id}>
                <CarouselSlide>
                  {/* Text section at the top with buttons */}
                  <SlideTextSection>
                    <SlideTextContent>
                      <div>
                        <SlideTagline>{slide.tagline}</SlideTagline>
                        <SlideTitle>{slide.title}</SlideTitle>
                      </div>
                      <SlideTextButtons>
                        <SlideTextPrimaryButton to={slide.primaryButtonLink}>
                          {slide.primaryButtonText}
                        </SlideTextPrimaryButton>
                      </SlideTextButtons>
                    </SlideTextContent>
                  </SlideTextSection>

                  {/* Media section - VIDEO or PHOTO based on slide */}
                  <SlideImageSection
                    mediaType={slide.mediaType}
                    bgImage={slide.mediaType === "image" ? slide.bgImage : null}
                  >
                    {slide.mediaType === "video" && slide.videoUrl && (
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        poster={slide.posterImage}
                      >
                        <source src={slide.videoUrl} type="video/mp4" />
                        {/* Fallback for browsers that don't support video */}
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </SlideImageSection>
                </CarouselSlide>
              </Carousel.Item>
            ))}
          </StyledCarousel>
        </div>
      </CarouselSection>

      <IconRowSection>
        <IconItem>
          <IconCircle>
            {/* Bundle + Save Icon - Replace with your own */}
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="#333"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </IconCircle>
          <IconLabel>Bundle + Save</IconLabel>
        </IconItem>

        <IconItem>
          <IconCircle>
            {/* Buy Now Pay Later Icon - Replace with your own */}
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="#333"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </IconCircle>
          <IconLabel>Buy Now, Pay Later</IconLabel>
        </IconItem>

        <IconItem>
          <IconCircle>
            {/* Free Shipping Icon - Replace with your own */}
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="#333"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 3h5l-1 4-4-4z" />
              <path d="M2 3h9l4 4v11a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V6z" />
              <path d="M8 12h.01" />
            </svg>
          </IconCircle>
          <IconLabel>Free Shipping + Returns</IconLabel>
        </IconItem>
      </IconRowSection>

      {/* Shop by Category Carousel Section */}
      <ShopByCategorySection>
        <CategoryContainer>
          <CategoryTitle1>Shop by category</CategoryTitle1>
          <CarouselWrapper>
            <LeftArrow show={showLeftArrow} onClick={scrollLeft}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </LeftArrow>

            <CategoriesScrollContainer ref={scrollRef} onScroll={handleScroll}>
              {shopCategoryData.map((category) => (
                <CategoryCardBox key={category.id} to={category.link}>
                  <CategoryIcon>
                    <img src={category.image} alt={category.name} />
                  </CategoryIcon>
                  <CategoryName>{category.name}</CategoryName>
                </CategoryCardBox>
              ))}
            </CategoriesScrollContainer>

            <RightArrow show={showRightArrow} onClick={scrollRight}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </RightArrow>
          </CarouselWrapper>

          {/* Mobile Progress Bar with Arrows */}
          <ProgressBarContainer>
            <ProgressBar>
              <ProgressFill progress={scrollProgress} />
            </ProgressBar>

            <ArrowButtonMobile onClick={scrollLeft} disabled={!showLeftArrow}>
              <AiOutlineArrowLeft />
            </ArrowButtonMobile>

            <ArrowButtonMobile onClick={scrollRight} disabled={!showRightArrow}>
              <AiOutlineArrowRight />
            </ArrowButtonMobile>
          </ProgressBarContainer>
        </CategoryContainer>
      </ShopByCategorySection>

      <ImageInSection>
        <div className="container">
          <img
            ref={imageRef}
            src={imageSrc}
            alt="STIHL Products"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "bottom",
            }}
            onLoad={() => {
              setImageLoaded(true);
              calculateHotspotPositions(); // Initial calculation after load
            }}
            onError={() => setImageLoaded(true)}
          />
          {imageLoaded &&
            responsiveImageHotspots.map((hotspot, index) => {
              const position = hotspotPositions[index] || {
                x: "50%",
                y: "50%",
              }; // Fallback if not calculated yet
              return (
                <ProductHotspot
                  key={hotspot.id}
                  style={{
                    left: position.x,
                    top: position.y,
                    transition: isResizing
                      ? "none"
                      : "left 0.3s ease, top 0.3s ease",
                  }}
                  isMobile={isMobile}
                  onClick={() =>
                    setActiveHotspot(
                      activeHotspot === hotspot.id ? null : hotspot.id,
                    )
                  }
                  data-hotspot
                >
                  {activeHotspot === hotspot.id && (
                    <ProductPopup>
                      <PopupContent>
                        <PopupImage
                          src={hotspot.product.image}
                          alt={hotspot.product.name}
                        />
                        <PopupDetails>
                          <PopupTitle>{hotspot.product.name}</PopupTitle>
                          <PopupPrice>{hotspot.product.price}</PopupPrice>
                          <PopupButton to={hotspot.product.link}>
                            View Product
                          </PopupButton>
                        </PopupDetails>
                      </PopupContent>
                    </ProductPopup>
                  )}
                </ProductHotspot>
              );
            })}
        </div>
      </ImageInSection>

      <CategoryGrid>
        <div className="container">
          <SectionTitle>Shop by Category</SectionTitle>
          {error && (
            <div
              style={{
                textAlign: "center",
                color: "#dc2626",
                marginBottom: "20px",
              }}
            >
              <p>⚠️ API Connection Issue: Showing sample data</p>
              <small>{error}</small>
            </div>
          )}
          <Grid>
            {(categoriesData.length > 0
              ? categoriesData.slice(0, 6)
              : categoryData
            ).map((category, index) => {
              const catData =
                categoryData.find(
                  (c) => c.title.toLowerCase() === category.name?.toLowerCase(),
                ) ||
                categoryData[index] ||
                categoryData[0];
              return (
                <CategoryCard
                  key={category.id || category.slug || index}
                  to={`/collections/${category.slug || catData.id}`}
                >
                  <CategoryImage
                    bgColor={catData.bgColor}
                    accentColor={catData.accentColor}
                  >
                    <img
                      src={category.image_url || catData.image}
                      alt={category.name || catData.title}
                    />
                  </CategoryImage>
                  <CategoryContent>
                    <CategoryTitle>
                      {category.name || catData.title}
                    </CategoryTitle>
                    <CategoryDescription>
                      {category.description || catData.description}
                    </CategoryDescription>
                    <CategoryLink>
                      Shop {category.name || catData.title} →
                    </CategoryLink>
                  </CategoryContent>
                </CategoryCard>
              );
            })}
          </Grid>
        </div>
      </CategoryGrid>

      <FeaturedSection>
        <div className="container">
          <SectionTitle>Featured Products</SectionTitle>
          <FeaturedGrid>
            {featuredProductsData.map((product, index) => (
              <FeaturedCard key={product.id || index}>
                <FeaturedImage>
                  <img
                    src={
                      product.images?.[0]?.image_url ||
                      product.image ||
                      "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=160&h=120&fit=crop"
                    }
                    alt={
                      product.images?.[0]?.alt_text || product.name || "Product"
                    }
                  />
                </FeaturedImage>
                <FeaturedContent>
                  <FeaturedTitle>{product.name || product.title}</FeaturedTitle>
                  <FeaturedDescription>
                    {product.short_description || product.description}
                  </FeaturedDescription>
                  <FeaturedLink to={`/products/${product.id || product.slug}`}>
                    {product.price ? `$${product.price}` : "Learn More"}
                  </FeaturedLink>
                </FeaturedContent>
              </FeaturedCard>
            ))}
          </FeaturedGrid>
        </div>
      </FeaturedSection>
    </HomePageWrapper>
  );
};

export default HomePage;
